import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getErrorTracker } from '@/lib/errors/factory';

/**
 * Props accepted by {@link ErrorBoundary}.
 *
 * @property children - Subtree whose render errors are caught.
 * @property fallback - Optional custom UI rendered instead of the default
 *   error card. Useful for embedding the boundary inside small widgets where
 *   the default panel would be too large.
 */
interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Global React error boundary (EXPAI-021).
 *
 * Catches uncaught render-phase errors thrown anywhere in its descendant tree
 * and forwards them to the active error tracking provider (Sentry when
 * configured via `VITE_SENTRY_DSN`, in-memory Noop fallback otherwise).
 *
 * The default fallback offers two recovery paths: a soft reset that clears
 * the boundary state and a hard `window.location.reload()`.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    getErrorTracker().captureException(error, {
      tags: { boundary: 'react' },
      extra: { componentStack: info.componentStack },
      level: 'error',
    });
  }

  private handleReset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <div className="max-w-md rounded-lg border border-border bg-card p-6 shadow-sm">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-6 w-6 text-destructive" />
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-foreground">Algo ha fallado</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Hemos registrado el error y nuestro equipo lo revisará.
              </p>
              <pre className="mt-3 max-h-32 overflow-auto rounded bg-muted p-2 text-xs text-muted-foreground">
                {this.state.error.message}
              </pre>
              <div className="mt-4 flex gap-2">
                <Button size="sm" onClick={this.handleReset}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Reintentar
                </Button>
                <Button size="sm" variant="outline" onClick={() => window.location.reload()}>
                  Recargar página
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
